// T038: PhotoUploadButton - Simplified upload interface for inline use
// Supports uploading both individual files and entire folders
import { useRef, useState, useEffect, useCallback } from 'react';
import { usePhotoUpload } from '@/hooks/usePhotoUpload';
import { Button, Input } from '@/components/atoms';
import { type ButtonProps } from '@/components/atoms/Button';
import { Modal } from '@/components/composed';
import { FileIcon, FolderIcon } from '@/components/icons';
import { supportsFolderPicker } from '@/utils/fileExtraction';
import { ACCEPTED_FILE_INPUT, filterValidFiles } from '@/utils/fileValidation';
import logger from '@/utils/logger';

export interface PhotoUploadButtonProps {
  onSuccess?: (photoId: string) => void;
  onBatchFilesSelected?: (files: File[]) => void;
  buttonText?: string;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  className?: string;
  /** If true, skip modal and go directly to file picker with multi-select */
  directMultiSelect?: boolean;
  /** If true, show folder upload option (only in directMultiSelect mode) */
  showFolderOption?: boolean;
  /** If true, disable the button (e.g., when another collection is uploading) */
  disabled?: boolean;
  /** Optional tooltip text shown on hover when disabled */
  disabledReason?: string;
}

export function PhotoUploadButton({
  onSuccess,
  onBatchFilesSelected,
  buttonText = 'Upload Photo',
  variant = 'secondary',
  size = 'compact',
  className = '',
  directMultiSelect = false,
  showFolderOption = true,
  disabled = false,
  disabledReason
}: PhotoUploadButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Check if folder picker is supported
  const folderSupported = supportsFolderPicker();

  const { uploadPhoto, uploadProgress, isUploading, resetUpload } = usePhotoUpload();

  // Callback ref to set webkitdirectory attribute when element mounts
  // React doesn't handle non-standard attributes well, so we set it imperatively
  const setFolderInputRef = useCallback((node: HTMLInputElement | null) => {
    if (node) {
      node.setAttribute('webkitdirectory', '');
      // Also set the legacy 'directory' attribute for broader compatibility
      node.setAttribute('directory', '');
    }
    // Store ref for later use (resetInputs, click triggering)
    (folderInputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
  }, []);

  const resetInputs = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (folderInputRef.current) {
      folderInputRef.current.value = '';
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawFiles = Array.from(event.target.files || []);

    if (rawFiles.length === 0) return;

    // Apply file validation (defense-in-depth for folder picker which may include non-image files)
    const { valid: files, invalid } = filterValidFiles(rawFiles);

    if (invalid.length > 0) {
      logger.info('PhotoUploadButton', `Filtered out ${invalid.length} invalid files from selection`, {
        invalidFiles: invalid.map(f => ({ name: f.file.name, reason: f.reason }))
      });
    }

    if (files.length === 0) {
      resetInputs();
      return;
    }

    // Direct multi-select mode: Always use batch upload callback
    if (directMultiSelect && onBatchFilesSelected) {
      onBatchFilesSelected(files);
      resetInputs();
      return;
    }

    // Legacy mode: Single file uses modal, multiple files use batch
    if (files.length === 1) {
      setSelectedFile(files[0]);
      // Auto-fill title from filename
      const filenameWithoutExt = files[0].name.replace(/\.[^/.]+$/, '');
      setTitle(filenameWithoutExt);
    }
    // Multiple files: Use batch upload callback
    else if (files.length > 1 && onBatchFilesSelected) {
      onBatchFilesSelected(files);
      resetInputs();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !title.trim()) return;

    const result = await uploadPhoto({
      title: title.trim(),
      file: selectedFile
    });

    if (result.success && result.photoId) {
      onSuccess?.(result.photoId);
      handleClose();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setTitle('');
    setSelectedFile(null);
    setShowUploadMenu(false);
    resetUpload();
    resetInputs();
  };

  const handleButtonClick = () => {
    if (disabled) return;

    if (directMultiSelect) {
      // Direct mode with folder option: Show menu
      if (showFolderOption && folderSupported) {
        setShowUploadMenu(!showUploadMenu);
      } else {
        // No folder option: Trigger file input directly
        fileInputRef.current?.click();
      }
    } else {
      // Legacy mode: Open modal
      setIsOpen(true);
    }
  };

  const handleSelectFiles = () => {
    setShowUploadMenu(false);
    fileInputRef.current?.click();
  };

  const handleSelectFolder = () => {
    setShowUploadMenu(false);
    folderInputRef.current?.click();
  };

  // Close menu when clicking outside
  useEffect(() => {
    if (!showUploadMenu) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUploadMenu(false);
      }
    };

    // Delay adding listener to prevent immediate close from the click that opened the menu
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showUploadMenu]);

  return (
    <>
      {/* Hidden file input for direct multi-select mode */}
      {directMultiSelect && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILE_INPUT}
            onChange={handleFileSelect}
            multiple
            className="hidden"
          />
          {/* Hidden folder input for folder selection - webkitdirectory set via callback ref */}
          {folderSupported && (
            <input
              ref={setFolderInputRef}
              type="file"
              accept={ACCEPTED_FILE_INPUT}
              onChange={handleFileSelect}
              className="hidden"
            />
          )}
        </>
      )}

      {/* Button with optional dropdown menu */}
      <div className="relative inline-block" title={disabled ? disabledReason : undefined}>
        <Button
          variant={variant}
          size={size}
          onClick={handleButtonClick}
          className={className}
          disabled={disabled}
        >
          {buttonText}
          {directMultiSelect && showFolderOption && folderSupported && !disabled && (
            <span className="ml-1">▾</span>
          )}
        </Button>

        {/* Dropdown menu for file/folder selection */}
        {showUploadMenu && (
          <div
            ref={menuRef}
            className="absolute left-0 mt-1 w-48 bg-surface-1 border border-border rounded-md shadow-lg z-50"
          >
            <button
              type="button"
              onClick={handleSelectFiles}
              className="w-full px-4 py-2 text-left text-sm hover:bg-surface-2 rounded-t-md flex items-center gap-2"
            >
              <FileIcon size={16} />
              Select Files
            </button>
            <button
              type="button"
              onClick={handleSelectFolder}
              className="w-full px-4 py-2 text-left text-sm hover:bg-surface-2 rounded-b-md flex items-center gap-2"
            >
              <FolderIcon size={16} />
              Select Folder
            </button>
          </div>
        )}
      </div>

      {!directMultiSelect && (
        <Modal
          open={isOpen}
          onClose={handleClose}
          title="Upload Photo"
        >
        <div className="space-y-4">
          <div>
            <label htmlFor="file-input" className="block mb-2 font-semibold">
              Select Photo *
            </label>
            <input
              ref={fileInputRef}
              id="file-input"
              type="file"
              accept={ACCEPTED_FILE_INPUT}
              onChange={handleFileSelect}
              disabled={isUploading}
              multiple
              className="w-full"
            />
            {selectedFile && (
              <p className="mt-2 text-sm text-muted">
                Selected: {selectedFile.name}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="title-input" className="block mb-2 font-semibold">
              Title *
            </label>
            <Input
              id="title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isUploading}
              maxLength={255}
              placeholder="Enter photo title"
            />
          </div>

          {uploadProgress && uploadProgress.progress > 0 && (
            <div className="space-y-2">
              <div className="w-full h-2 bg-surface-2 rounded overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${uploadProgress.progress}%` }}
                />
              </div>
              <p className="text-sm text-muted">
                {uploadProgress.status === 'uploading' ? 'Uploading...' :
                 uploadProgress.status === 'processing' ? 'Processing...' :
                 uploadProgress.status === 'complete' ? 'Complete!' : 'Preparing...'}
              </p>
            </div>
          )}

          {uploadProgress?.status === 'error' && uploadProgress.error && (
            <p className="text-sm text-danger">{uploadProgress.error}</p>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={handleClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!selectedFile || !title.trim() || isUploading}
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </div>
        </Modal>
      )}
    </>
  );
}
